import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import {
    lazy,
    type ReactNode,
    Suspense,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { z } from 'zod';

import { AdminCommandPalette } from '@apps/web/components/admin/admin_command_palette';
import { ModeToggle } from '@apps/web/components/mode_toggle';
import {
    DEFAULT_PHOEBE_CHAT_DEBUG_TAB,
    PHOEBE_CHAT_DEBUG_TAB_VALUES,
    PhoebeChatDebugSidebar,
    type PhoebeChatDebugTab,
} from '@apps/web/components/phoebe_chat_debug_sidebar';
import { type PhoebeChatDebugSnapshot } from '@apps/web/components/phoebe_chat_debug_snapshot';
import {
    PhoebeChatView,
    StructuredReportArtifactsPanel,
} from '@apps/web/components/phoebe_chat_view';
import { Button } from '@apps/web/components/ui/button';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@apps/web/components/ui/resizable';
import {
    type SessionStateContextType,
    useSessionState,
} from '@apps/web/context/session_state_provider';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    createAgentChatDestinationResolver,
    normalizeAgentChatSearch,
} from '@apps/web/lib/agent_chat_navigation';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type User } from '@libraries/typescript/api_client';

const DEFAULT_CHAT_PANEL_SIZE = 62;
const DEFAULT_DEBUG_PANEL_SIZE = 38;
const MIN_DEBUG_PANEL_SIZE = 20;
const UUID_SCHEMA = z.uuid();
const resolveAgentChatDestination = createAgentChatDestinationResolver();

const LegacyAgentHost = lazy(async function loadLegacyAgentHost() {
    const module =
        await import('@apps/web/routes/_app/(agent)/agent/-legacy_host');
    return { default: module.LegacyAgentHost };
});

export const agentSearchSchema = z.object({
    chatId: z.string().optional(),
    debugTab: z.enum(PHOEBE_CHAT_DEBUG_TAB_VALUES).optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

export function requirePlatformAdmin(context: {
    readonly sessionState: Pick<SessionStateContextType, 'session'> | null;
    readonly user: Pick<User, 'admin'> | null;
}): void {
    if (!context.sessionState?.session || context.user?.admin !== true) {
        throw redirect({ to: '/home' });
    }
}

export const Route = createFileRoute('/_app/(agent)/agent/')({
    component: AgentIndexPage,
    validateSearch: agentSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Agent Debug | Phoebe',
            },
        ],
    }),
});

function AgentIndexPage() {
    const search = Route.useSearch();
    if (search.chatId === undefined) {
        return <LegacyAgentBranch />;
    }
    if (!UUID_SCHEMA.safeParse(search.chatId).success) {
        return <AgentCompatibilityError />;
    }
    return <AgentCandidateBranch chatId={search.chatId} />;
}

type CandidateResolutionState =
    | { readonly kind: 'pending' }
    | { readonly kind: 'panel' }
    | { readonly kind: 'error' };

function AgentCandidateBranch({ chatId }: { readonly chatId: string }) {
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const selectedOrganizationId = useOrganizationId();
    const { session } = useSessionState();
    const organizationId = search.organizationId ?? selectedOrganizationId;
    const mode = search.mode ?? 'live';
    const { data: organization, error: organizationError } = useOrganization(
        organizationId,
        { reason: 'agent-chat-compatibility' },
        mode,
    );
    const [resolutionState, setResolutionState] =
        useState<CandidateResolutionState>({ kind: 'pending' });

    useEffect(() => {
        if (
            session === null ||
            organizationId === null ||
            organizationId.length === 0 ||
            organizationError !== undefined ||
            organization === undefined
        ) {
            return;
        }

        let active = true;
        setResolutionState({ kind: 'pending' });
        void resolveAgentChatDestination({
            chatId,
            organizationId,
            mode,
        })
            .then((resolution) => {
                if (!active || resolution.kind === 'stale') {
                    return;
                }
                if (resolution.destination.kind === 'run') {
                    const navigationSearch = normalizeAgentChatSearch(search);
                    if (
                        navigationSearch.organizationId === undefined ||
                        navigationSearch.mode === undefined
                    ) {
                        setResolutionState({ kind: 'error' });
                        return;
                    }
                    void navigate({
                        to: '/agent/runs/$runId',
                        params: { runId: resolution.destination.runId },
                        search: {
                            organizationId: navigationSearch.organizationId,
                            mode: navigationSearch.mode,
                        },
                        replace: true,
                    });
                    return;
                }
                setResolutionState({ kind: 'panel' });
            })
            .catch(() => {
                if (active) {
                    setResolutionState({ kind: 'error' });
                }
            });

        return () => {
            active = false;
        };
    }, [
        chatId,
        mode,
        navigate,
        organization,
        organizationError,
        organizationId,
        search,
        session,
    ]);

    if (
        session === null ||
        organizationId === null ||
        organizationId.length === 0
    ) {
        return <AgentCompatibilityError />;
    }
    if (organizationError !== undefined) {
        return <AgentCompatibilityError />;
    }
    if (organization === undefined) {
        return <AgentCompatibilityPending />;
    }
    if (resolutionState.kind === 'panel') {
        return <LegacyAgentBranch />;
    }
    if (resolutionState.kind === 'error') {
        return <AgentCompatibilityError />;
    }
    return <AgentCompatibilityPending />;
}

function AgentCompatibilityPending() {
    return <div>Checking chat compatibility…</div>;
}

function AgentCompatibilityError() {
    return (
        <div>
            Something went wrong opening this conversation. Please try again.
        </div>
    );
}

function LegacyAgentBranch() {
    requirePlatformAdmin(Route.useRouteContext());
    return (
        <Suspense fallback={<AgentCompatibilityPending />}>
            <LegacyAgentHost>
                <AgentDebugPage />
            </LegacyAgentHost>
        </Suspense>
    );
}

function AgentDebugPage(): ReactNode {
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'agent-debug-page',
    });
    const { chatId: urlChatId, debugTab } = Route.useSearch();
    const navigate = Route.useNavigate();
    const [chatId, setChatId] = useState<string | null>(urlChatId ?? null);
    const [chatDebugSnapshot, setChatDebugSnapshot] =
        useState<PhoebeChatDebugSnapshot | null>(null);

    useEffect(() => {
        setChatId(urlChatId ?? null);
    }, [urlChatId]);

    const handleSelectedConversationIdChange = useCallback(
        (nextChatId: string | null) => {
            setChatId(nextChatId);
            void navigate({
                to: '/agent',
                search: (previous) => ({
                    ...previous,
                    chatId: nextChatId ?? undefined,
                }),
                replace: true,
            });
        },
        [navigate],
    );

    const handleDebugTabChange = useCallback(
        (nextTab: PhoebeChatDebugTab) => {
            void navigate({
                to: '/agent',
                search: (previous) => ({
                    ...previous,
                    debugTab:
                        nextTab === DEFAULT_PHOEBE_CHAT_DEBUG_TAB
                            ? undefined
                            : nextTab,
                }),
                replace: true,
            });
        },
        [navigate],
    );

    return (
        <div className="bg-background flex h-full min-h-0 flex-col overflow-hidden">
            <AdminCommandPalette />
            <header className="flex shrink-0 items-center gap-3 border-b px-3 py-2">
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link to="/home">
                        <ArrowLeft className="size-4" />
                        Back to app
                    </Link>
                </Button>
                <div className="min-w-0 flex-1">
                    <h1 className="text-sm font-semibold">Agent debug</h1>
                    <p className="text-muted-foreground truncate text-xs">
                        {organization?.name ?? 'Loading organization…'}
                    </p>
                </div>
                <div className="w-44">
                    <ModeToggle />
                </div>
            </header>
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <ResizablePanelGroup
                    orientation="horizontal"
                    className="h-full min-h-0 w-full overflow-hidden"
                    defaultLayout={{
                        'chat-panel': DEFAULT_CHAT_PANEL_SIZE,
                        'debug-panel': DEFAULT_DEBUG_PANEL_SIZE,
                    }}
                >
                    <ResizablePanel
                        id="chat-panel"
                        defaultSize={DEFAULT_CHAT_PANEL_SIZE}
                        className="min-h-0 overflow-hidden"
                    >
                        <div className="flex h-full min-h-0 flex-col">
                            <div className="flex min-h-0 flex-1">
                                <section className="flex min-w-0 flex-1 flex-col">
                                    <div className="min-h-0 flex-1">
                                        <PhoebeChatView
                                            target={{ kind: 'general' }}
                                            mode="general"
                                            conversationId={chatId}
                                            onSelectedConversationIdChange={
                                                handleSelectedConversationIdChange
                                            }
                                            onDebugSnapshotChange={
                                                setChatDebugSnapshot
                                            }
                                            debugPanelOpen={true}
                                        />
                                    </div>
                                </section>
                            </div>
                        </div>
                    </ResizablePanel>
                    <ResizableHandle withHandle={true} />
                    <ResizablePanel
                        id="debug-panel"
                        defaultSize={DEFAULT_DEBUG_PANEL_SIZE}
                        minSize={MIN_DEBUG_PANEL_SIZE}
                        className="min-h-0 overflow-hidden"
                    >
                        <PhoebeChatDebugSidebar
                            snapshot={chatDebugSnapshot}
                            tab={debugTab ?? DEFAULT_PHOEBE_CHAT_DEBUG_TAB}
                            onTabChange={handleDebugTabChange}
                        />
                    </ResizablePanel>
                </ResizablePanelGroup>
                <StructuredReportArtifactsPanel splitMode={false} />
            </div>
        </div>
    );
}
